import { apiFetch, type PortalDashboard, type PortalSignableDocument } from "@dearbill/shared";

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? "";

/**
 * The CMS-10882 registry id. Its signing keeps the dashboard's own M3P flow
 * (`PortalSign` and the Action Needed M3P row), so the generic document
 * surfaces skip it.
 */
export const M3P_DOCUMENT_ID = "cms_10882";

/**
 * The URL slug for a registry id, as the signature-request links carry it:
 * `cms_1696` is `/forms/{id}/sign/cms-1696`.
 */
export function documentSlug(documentId: string): string {
  return documentId.replaceAll("_", "-");
}

/** The member-portal sign route for one document. */
export function documentSignPath(enrollmentId: string, documentId: string): string {
  return `/forms/${encodeURIComponent(enrollmentId)}/sign/${documentSlug(documentId)}`;
}

/**
 * True for an unsigned document that only someone other than this session may
 * sign, such as CMS-1696 in a caregiver session. For a document the session
 * can't sign, the server names the signer who would sign it, so a `signedAs`
 * that differs from the session's own signer role marks it.
 */
export function signedByAnother(
  doc: PortalSignableDocument,
  role: PortalDashboard["role"],
): boolean {
  const sessionSigner = role === "caregiver" ? "caregiver" : "patient";
  return doc.status !== "signed" && !doc.signable && doc.signedAs !== sessionSigner;
}

/** The API base path of one enrollment's documents; `enrollmentId` may be the full id or the short id. */
export function documentsApiPath(enrollmentId: string): string {
  return `/api/portal/enrollments/${encodeURIComponent(enrollmentId)}/documents`;
}

/**
 * `GET .../documents` with the portal bearer. A non-array body reads as no
 * documents, so a malformed response hides the document surfaces rather than
 * breaking the page. Errors (an `ApiError` 401 or 404 included) propagate.
 */
export async function fetchDocuments(
  enrollmentId: string,
  session: string,
): Promise<PortalSignableDocument[]> {
  const docs = await apiFetch<unknown>(
    documentsApiPath(enrollmentId),
    {},
    {
      baseUrl: API_BASE,
      token: session,
    },
  );
  // A narrowing cast: the body is the hand-mirrored wire list once it is an array.
  return Array.isArray(docs) ? (docs as PortalSignableDocument[]) : [];
}
